'use client';

import React, { useState, useEffect } from 'react';
import { fetchCashflowLiquidityAnalysis } from '../../apiHelper';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { Chart } from 'react-google-charts';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';

const formatCurrency = (val) => {
    if (!val) return '₦0';
    if (val >= 1000000) return `₦${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `₦${(val / 1000).toFixed(1)}K`;
    return `₦${val.toLocaleString()}`;
};

const CashFlowLiquidityDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        start_date: '2026-03-01',
        end_date: '2026-04-13',
    });

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const result = await fetchCashflowLiquidityAnalysis(filters);
                setData(result);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [filters]);

    const cash = data || {};
    const netFlow = cash.net_cash_flow || 0;
    const isPositive = netFlow >= 0;

    // Prepare Sankey Data for Google Charts
    const sankeyData = React.useMemo(() => {
        if (!cash.sankey || !cash.sankey.nodes || !cash.sankey.links) {
            return [["From", "To", "Weight"]];
        }

        const chartData = [["From", "To", "Weight"]];

        cash.sankey.links.forEach(link => {
            chartData.push([link.source, link.target, Math.round(link.value)]);
        });

        return chartData;
    }, [cash.sankey]);

    return (
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">Cash Flow & Liquidity</h2>
                    <p className="text-gray-500 text-xs mt-1">Cash health • Runway • Liquidity position</p>
                </div>

                <div className="flex gap-4 items-center">
                    <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-2xl">
                        <input
                            type="date"
                            value={filters.start_date}
                            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                            className="bg-transparent outline-none text-sm"
                        />
                        <span className="text-gray-400">→</span>
                        <input
                            type="date"
                            value={filters.end_date}
                            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                            className="bg-transparent outline-none text-sm"
                        />
                    </div>

                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-3 bg-[#4285F4] text-white rounded-2xl hover:bg-blue-600 transition font-medium flex items-center gap-2"
                    >
                        <RefreshCw size={18} /> Refresh
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <div className="bg-gradient-to-br from-emerald-600 to-teal-600 text-white p-7 rounded-3xl">
                    <p className="text-emerald-100 text-sm font-medium">CURRENT CASH</p>
                    <p className="text-3xl font-bold mt-3 tracking-tighter leading-none">
                        {formatCurrency(cash.current_cash_balance)}
                    </p>
                    <p className="text-xs text-emerald-200 mt-6">Total across bank accounts</p>
                </div>

                <div className="bg-white border border-gray-200 p-7 rounded-3xl">
                    <p className="text-gray-500 text-sm font-medium">NET CASH FLOW</p>
                    <p className={`text-3xl font-bold mt-3 tracking-tighter leading-none ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                        {isPositive ? '+' : ''}{formatCurrency(Math.abs(netFlow))}
                    </p>
                    <p className="text-xs text-gray-400 mt-6">In selected period</p>
                </div>

                <div className="bg-white border border-gray-200 p-7 rounded-3xl">
                    <p className="text-gray-500 text-sm font-medium">CASH RUNWAY</p>
                    <p className="text-3xl font-bold mt-3 tracking-tighter leading-none text-gray-800">
                        {cash.cash_runway_days || 0} <span className="text-2xl font-normal">days</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-6">At current burn rate</p>
                </div>

                <div className="bg-white border border-gray-200 p-7 rounded-3xl">
                    <p className="text-gray-500 text-sm font-medium">QUICK RATIO</p>
                    <p className="text-3xl font-bold mt-3 tracking-tighter text-gray-800">
                        {cash.quick_ratio?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-xs text-gray-400 mt-6">Current Assets / Liabilities</p>
                </div>
            </div>

            {/* Cash Flow Trend */}
            {/* <div className="flex-1 bg-white border border-gray-100 rounded-3xl p-8 mb-8">
                <h3 className="font-semibold mb-6">Cash Flow Trend</h3>
                {cash.cash_flow_trend && cash.cash_flow_trend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={340}>
                        <LineChart data={cash.cash_flow_trend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="date" stroke="#888" fontSize={12} />
                            <YAxis stroke="#888" fontSize={12} />
                            <Tooltip />
                            <Line 
                                type="natural" 
                                dataKey="net" 
                                stroke="#10b981" 
                                strokeWidth={4} 
                                dot={{ fill: '#10b981', r: 4 }}
                                activeDot={{ r: 7 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-[340px] flex items-center justify-center text-gray-400">
                        No cash flow data available for this period
                    </div>
                )}
            </div> */}

            {/* Sankey Diagram */}
            <div className="bg-white border border-gray-100 rounded-3xl p-8">
                <h3 className="font-semibold mb-6">Cash Flow Sankey Diagram</h3>
                {cash.sankey && cash.sankey.links && cash.sankey.links.length > 0 ? (
                    <Chart
                        chartType="Sankey"
                        width="100%"
                        height="420px"
                        data={sankeyData}
                        options={{
                            sankey: {
                                node: {
                                    colors: ['#4285F4', '#34A853', '#FBBC05', '#EA4335'],
                                    nodePadding: 12,
                                    label: { fontSize: 13, color: '#333', bold: true }
                                },
                                link: {
                                    colorMode: 'gradient',
                                    colors: ['#4285F4', '#34A853']
                                }
                            },
                            tooltip: { isHtml: true }
                        }}
                    />
                ) : (
                    <div className="h-[420px] flex items-center justify-center text-gray-400 border border-dashed rounded-2xl">
                        No Sankey data available
                    </div>
                )}
            </div>
        </div>
    );
};

export default CashFlowLiquidityDashboard;